import React, { useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ArrowUpRight, BookOpen } from 'lucide-react';

const BlogSection = () => {
  const containerRef = useRef(null);

  const posts = [
    {
      category: "Health",
      date: "Mar 18, 2026",
      title: "Essential Vaccination Protocols for Puppies",
      excerpt: "Ensure your pet's protection against Rabies, Parvovirus, and more with our comprehensive guide.",
      image: "https://images.unsplash.com/photo-1576201836106-db1758fd1c97?q=80&w=800&auto=format&fit=crop"
    },
    {
      category: "Surgery",
      date: "Feb 22, 2026",
      title: "Post-Operative Care: What to Expect",
      excerpt: "A guardian's manual to navigating your dog's recovery phase efficiently at home.",
      image: "https://images.unsplash.com/photo-1599443015574-be5fe8a05783?q=80&w=800&auto=format&fit=crop"
    }
  ];

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.blog-reveal', 
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 1,
          ease: "expo.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="bg-secondary/20 relative overflow-hidden" id="blog">
      <div className="section-container">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 lg:mb-24 gap-8">
          <div className="space-y-6">
            <div className="flex items-center gap-4">
               <span className="w-12 h-px bg-primary/30"></span>
               <span className="blog-reveal text-primary font-bold tracking-[0.2em] uppercase text-[10px] md:text-[11px] block">Resource Hub</span>
               <span className="w-12 h-px bg-primary/30"></span>
            </div>
            <h2 className="blog-reveal text-navy font-display font-bold leading-tight">
               Clinical <br /> <span className="text-primary italic font-normal">Insights.</span>
            </h2>
          </div>
          <Link to="/blog" className="blog-reveal flex flex-col items-center justify-center w-28 h-28 rounded-full border border-navy/10 hover:bg-navy hover:text-white hover:border-navy transition-all duration-500 group">
            <ArrowUpRight size={24} className="mb-1 group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-black uppercase tracking-widest text-center leading-none">View All<br/>Articles</span>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 relative z-10">
          {posts.map((post, index) => (
            <div key={index} className="blog-reveal p-6 md:p-8 lg:p-10 rounded-[2.5rem] bg-white border border-navy/5 shadow-glass-light group hover:shadow-glass-heavy transition-all duration-700 cursor-pointer overflow-hidden relative">
              {/* Cinematic Blur */}
              <div className="absolute -bottom-20 -right-20 w-48 h-48 bg-primary/5 rounded-full blur-[60px] group-hover:bg-primary/10 transition-all duration-1000 -z-10"></div>
              
              <div className="flex flex-col xl:flex-row gap-8 lg:gap-10">
                <div className="w-full xl:w-2/5 aspect-[4/3] xl:aspect-square rounded-[2rem] overflow-hidden relative shadow-md">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-[2s]" />
                  <div className="absolute top-4 left-4 px-4 py-2 bg-white/95 backdrop-blur-md rounded-full text-[9px] font-black uppercase tracking-[0.2em] flex items-center gap-2 shadow-sm text-primary">
                    <BookOpen size={12} />
                    {post.category}
                  </div>
                </div>
                <div className="w-full xl:w-3/5 py-4 flex flex-col justify-center">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-navy/30 mb-4">{post.date}</p>
                  <h3 className="font-display font-bold leading-tight mb-5 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-navy/60 text-sm leading-relaxed line-clamp-2 font-medium">
                    {post.excerpt}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
